import React from 'react';

import {
  InformationPage,
  OrganizationDetailsPage,
  SubdivisionDetailsPage,
  SubdivisionsPage,
  orgDetailsChildren,
  rolesChildren,
  subDetailsChildren,
} from '@gasbi/shared/web/Organization';
import {
  EmployeeDetails,
  MyOrganizationEmployees,
} from '@gasbi/shared/web/RbacOrganization';
import { Outlet } from 'react-router-dom';

import Pages from '@/pages';
import OrganizationApiContextWrapper from '@/providers/OrganizationApiContextWrapper';
import RbacOrganizationApiWrapper from '@/providers/RbacOrganizationApiWrapper';
import type { AppRouteObject } from '@/routeTypes';

const MyOrganization = React.lazy(() => import('@/pages/MyOrganization'));
const OrganizationInformation = React.lazy(
  () => import('@/pages/OrganizationInformation'),
);
const Organizations = React.lazy(() => import('@/pages/Organizations'));
const SubdivisionEmployeeDetails = React.lazy(
  () => import('@/pages/SubdivisionEmployeeDetails'),
);

/**
 * The organization area (GSB-8317, PO 2026-10-26: «як у Supply»). The current organization is the shared RBAC page
 * with BMS's tabs; the ГРМ card (on «Інформація», read-only, PO 2026-10-27), structural subdivisions, roles and the
 * organizations list stay on the legacy @gasbi/shared/web/Organization pages. BMS has no head office view any more:
 * it is Distribution Admin's.
 */
export const organizationRoutes: AppRouteObject[] = [
  {
    element: (
      <RbacOrganizationApiWrapper>
        <OrganizationApiContextWrapper>
          <MyOrganization />
        </OrganizationApiContextWrapper>
      </RbacOrganizationApiWrapper>
    ),
    children: [
      {
        element: <OrganizationInformation />,
        children: [{ index: true, element: <InformationPage /> }],
      },
      { path: Pages.SUBDIVISIONS_PART, element: <SubdivisionsPage /> },
      { path: Pages.EMPLOYEES_PART, element: <MyOrganizationEmployees /> },
    ],
  },
  {
    path: `${Pages.EMPLOYEES_PART}/:employeeId`,
    element: (
      <RbacOrganizationApiWrapper>
        <EmployeeDetails />
      </RbacOrganizationApiWrapper>
    ),
  },
  {
    element: (
      <OrganizationApiContextWrapper>
        <RbacOrganizationApiWrapper>
          <Outlet />
        </RbacOrganizationApiWrapper>
      </OrganizationApiContextWrapper>
    ),
    children: [
      {
        path: `${Pages.SUBDIVISIONS_PART}/:subId`,
        element: <SubdivisionDetailsPage />,
        children: subDetailsChildren,
      },
      {
        path: `${Pages.SUBDIVISIONS_PART}/:subId/${Pages.EMPLOYEES_PART}/:employeeId`,
        element: <SubdivisionEmployeeDetails />,
      },
      {
        path: Pages.ROLES_PART,
        element: <Outlet />,
        children: rolesChildren(false),
      },
      {
        path: Pages.ORGANIZATIONS_PART,
        element: <Organizations />,
        handle: { keepAlive: true },
      },
      {
        path: `${Pages.ORGANIZATIONS_PART}/:orgId`,
        element: <OrganizationDetailsPage />,
        children: orgDetailsChildren(),
        handle: { keepAlive: true },
      },
      {
        path: `${Pages.ORGANIZATIONS_PART}/:orgId/${Pages.SUBDIVISIONS_PART}/:subId`,
        element: <SubdivisionDetailsPage />,
        children: subDetailsChildren,
      },
      {
        path: `${Pages.ORGANIZATIONS_PART}/:orgId/${Pages.SUBDIVISIONS_PART}/:subId/${Pages.EMPLOYEES_PART}/:employeeId`,
        element: <SubdivisionEmployeeDetails />,
      },
      {
        path: `${Pages.ORGANIZATIONS_PART}/:orgId/${Pages.ROLES_PART}`,
        element: <Outlet />,
        children: rolesChildren(false),
      },
    ],
  },
];
